import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useGoogleLogin } from "@react-oauth/google";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Mail, Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const GOOGLE_ENABLED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const { login, googleSignIn, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const next = location.state?.from?.pathname || null;

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}`);
      navigate(next || (u.role === "admin" ? "/admin" : u.role === "ca" ? "/ca-portal" : "/dashboard"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    }
  };

  const demoLogin = async (role) => {
    const map = {
      taxpayer: ["taxpayer@demo.in", "Demo@1234"],
      ca: ["ritika@caritikasingh.com", "Ritika@1234"],
      admin: ["admin@caritikasingh.com", "Admin@1234"],
    };
    setEmail(map[role][0]); setPassword(map[role][1]);
    try {
      const u = await login(map[role][0], map[role][1]);
      toast.success(`Signed in as ${u.full_name}`);
      navigate(u.role === "admin" ? "/admin" : u.role === "ca" ? "/ca-portal" : "/dashboard");
    } catch {
      toast.error("Demo login failed");
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      try {
        const u = await googleSignIn(access_token);
        toast.success(`Welcome, ${u.full_name.split(" ")[0]}`);
        navigate(next || (u.role === "admin" ? "/admin" : u.role === "ca" ? "/ca-portal" : "/dashboard"));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
      }
    },
    onError: () => toast.error("Google sign-in failed. Please try again."),
  });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary-foreground text-primary font-display font-bold">R</div>
          <div className="font-display text-lg font-bold tracking-tight">CA Ritika Singh</div>
        </Link>
        <div className="relative max-w-md">
          <ShieldCheck className="h-10 w-10 mb-6 text-accent" />
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            Welcome back. Your tax data is right where you left it.
          </h2>
          <p className="mt-4 text-primary-foreground/75 leading-relaxed">
            Encrypted with AES-256. Hosted in Indian data centres. Audited regularly.
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/60">© {new Date().getFullYear()} CA Ritika Singh · Made in India</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">Welcome back. File where you left off.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground hover:underline">Forgot password?</Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" data-testid="login-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-9" placeholder="Minimum 8 characters" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          {GOOGLE_ENABLED && (
            <>
              <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <div>OR</div>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button variant="outline" className="w-full h-11" onClick={() => googleLogin()} data-testid="login-google">
                <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.47 12c0-.73.13-1.45.36-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Continue with Google
              </Button>
            </>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            New here? <Link to="/signup" className="font-semibold text-foreground hover:underline" data-testid="login-to-signup">Create an account</Link>
          </p>

          <div className="mt-10 rounded-lg border border-dashed border-border p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Demo accounts</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" type="button" onClick={() => demoLogin("taxpayer")} data-testid="demo-taxpayer">Taxpayer</Button>
              <Button variant="outline" size="sm" type="button" onClick={() => demoLogin("ca")} data-testid="demo-ca">Chartered Accountant</Button>
              <Button variant="outline" size="sm" type="button" onClick={() => demoLogin("admin")} data-testid="demo-admin">Admin</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
